import { useCallback, useEffect, useState } from "react";
import api, { downloadBlob } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Book, Download, FileSpreadsheet, Search, ExternalLink } from "lucide-react";

function OssLink({ href, label, testid }) {
  if (!href) return <span className="text-muted-foreground">—</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1" data-testid={testid}>
      {label}<ExternalLink size={11} />
    </a>
  );
}

const CATS = [
  { v: "", label: "All" },
  { v: "rsaw", label: "RSAW" },
  { v: "technical_justification", label: "Technical Justification" },
  { v: "evidence_request_tool", label: "Evidence Request Tool" },
  { v: "implementation_plan", label: "Implementation Plan" },
  { v: "other", label: "Other" },
];
const catLabel = (v) => (CATS.find((c) => c.v === v)?.label || v);
const statusCls = (s) => {
  const l = (s || "").toLowerCase();
  if (l.includes("inactive")) return "bg-slate-100 text-slate-600 border-slate-200";
  if (l.includes("future") || l.includes("pending")) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-emerald-100 text-emerald-800 border-emerald-200";
};

export default function NercLibrary() {
  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="nerc-library-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Book className="text-primary" /> NERC Library</h1>
          <p className="text-sm text-muted-foreground mt-1">Reference material and the NERC One Stop Shop registry, maintained by Claro Compliance.</p>
        </div>
        <Tabs defaultValue="library">
          <TabsList className="rounded-none">
            <TabsTrigger value="library" className="rounded-none" data-testid="nlib-tab-library">Reference Library</TabsTrigger>
            <TabsTrigger value="oss" className="rounded-none" data-testid="nlib-tab-oss">One Stop Shop</TabsTrigger>
          </TabsList>
          <TabsContent value="library" className="mt-4"><LibraryTab /></TabsContent>
          <TabsContent value="oss" className="mt-4"><OssTab /></TabsContent>
        </Tabs>
    </div>
  );
}

function LibraryTab() {
  const [files, setFiles] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const load = useCallback(() => {
    api.get("/nerc-library", { params: cat ? { category: cat } : {} }).then(({ data }) => setFiles(data.files)).catch(() => {});
  }, [cat]);
  useEffect(() => { load(); }, [load]);
  const shown = files.filter((f) => {
    if (!q) return true;
    const t = `${f.title} ${f.standard_number} ${f.filename} ${f.description}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {CATS.map((c) => (
            <Button key={c.v || "all"} size="sm" variant={cat === c.v ? "default" : "outline"} className="rounded-none h-8" onClick={() => setCat(c.v)} data-testid={`nlib-cat-${c.v || "all"}`}>{c.label}</Button>
          ))}
        </div>
        <div className="relative ml-auto"><Search size={14} className="absolute left-2 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="rounded-none h-9 pl-7 w-56" data-testid="nlib-search" /></div>
      </div>
      <div className="space-y-1.5">
        {shown.map((x) => (
          <div key={x.id} className="flex items-center gap-3 border border-border bg-white px-3 py-2" data-testid={`nlib-file-${x.id}`}>
            <span className="px-1.5 py-0.5 border rounded-sm text-[10px] font-semibold bg-slate-100 text-slate-700 uppercase">{catLabel(x.category)}</span>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{x.title}</div>
              <div className="text-xs text-muted-foreground truncate">{x.standard_number ? `${x.standard_number} · ` : ""}{x.filename}{x.description ? ` — ${x.description}` : ""}</div>
            </div>
            <Button size="sm" variant="outline" className="rounded-none" onClick={() => downloadBlob(`/nerc-library/${x.id}/download`, x.filename)} data-testid={`nlib-dl-${x.id}`}><Download size={14} className="mr-1" /> Download</Button>
          </div>
        ))}
        {shown.length === 0 && <div className="text-center text-muted-foreground text-sm py-10 border border-dashed border-border">No documents available in this category yet.</div>}
      </div>
    </div>
  );
}

function OssTab() {
  const [data, setData] = useState({ rows: [], total: 0, families: [], statuses: [] });
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("all");
  const [status, setStatus] = useState("all");
  const load = useCallback(() => {
    const params = { limit: 300 };
    if (q) params.q = q;
    if (family !== "all") params.family = family;
    if (status !== "all") params.status = status;
    api.get("/one-stop-shop", { params }).then(({ data }) => setData(data)).catch(() => {});
  }, [q, family, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><FileSpreadsheet size={14} className="absolute left-2 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number, title, purpose…" className="rounded-none h-9 pl-7 w-72" data-testid="oss-search" /></div>
        <Select value={family} onValueChange={setFamily}>
          <SelectTrigger className="rounded-none h-9 w-36" data-testid="oss-family"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All families</SelectItem>
            {data.families.map((fam) => <SelectItem key={fam} value={fam}>{fam}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-none h-9 w-56" data-testid="oss-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(data.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-muted-foreground">{data.total} standards</span>
      </div>
      <div className="border border-border overflow-x-auto bg-white max-h-[65vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0">
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="p-2">Status</th>
              <th className="p-2">Standard</th>
              <th className="p-2">Title</th>
              <th className="p-2 whitespace-nowrap">FERC Order</th>
              <th className="p-2 whitespace-nowrap">Effective Date</th>
              <th className="p-2">Impl Plan</th>
              <th className="p-2">Project Page</th>
              <th className="p-2">RSAW</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/40 align-top" data-testid={`oss-row-${r.standard_number}`}>
                <td className="p-2"><span className={`px-1.5 py-0.5 border rounded-sm text-[10px] font-semibold whitespace-nowrap ${statusCls(r.status)}`}>{r.status || "—"}</span></td>
                <td className="p-2 font-mono text-xs whitespace-nowrap">
                  {r.standard_link
                    ? <a href={r.standard_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`oss-std-link-${r.standard_number}`}>{r.standard_number}<ExternalLink size={11} /></a>
                    : r.standard_number}
                </td>
                <td className="p-2 min-w-[200px]">{r.standard_title}</td>
                <td className="p-2 text-xs whitespace-nowrap">{r.ferc_orders || "—"}</td>
                <td className="p-2 text-xs whitespace-nowrap">{r.effective_date || "—"}</td>
                <td className="p-2 text-xs whitespace-nowrap"><OssLink href={r.impl_plan_link} label="Plan" testid={`oss-impl-${r.standard_number}`} /></td>
                <td className="p-2 text-xs"><OssLink href={r.project_page_link} label={r.project_page_label || "Project"} testid={`oss-proj-${r.standard_number}`} /></td>
                <td className="p-2 text-xs whitespace-nowrap"><OssLink href={r.rsaw_link} label="RSAW" testid={`oss-rsaw-${r.standard_number}`} /></td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground text-sm">No standards match — the One Stop Shop may not be imported yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
